import { Collapse, Divider, ListItemButton, ListItemIcon, ListItemText, Stack, type SxProps } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMatchRoute } from '@tanstack/react-router';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  LogOutIcon,
  SendHorizontalIcon,
  ShieldCheckIcon,
  UserRoundCogIcon,
  UsersRoundIcon,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '@/components/Icon/Icon';
import { useAuthenticatedUser, useUserProfile } from '@/routes/_authenticated.hooks';
import { useRouteContextQuery } from '@/routes/_authenticated/_dashboard.hooks';
import { useSelectedAccount } from '@/routes/_authenticated/_dashboard/accounts/$accountId/route.hooks';
import { Nav, NavItem } from './DashboardNavCommon';

const sxTypography: SxProps = {
  textOverflow: 'ellipsis',
  textWrap: 'nowrap',
  overflowX: 'hidden',
  whiteSpace: 'nowrap',
};

const NavItemToggleIcon = ({ open }: { open: boolean }) => (
  <ListItemIcon sx={{ mr: 0 }}>
    <Icon icon={open ? ChevronUpIcon : ChevronDownIcon} size={20} />
  </ListItemIcon>
);

export const DashboardUserNavigation = ({ onOpen }: { onOpen: () => void }) => {
  const account = useSelectedAccount();

  const userProfile = useUserProfile();
  const { email } = useAuthenticatedUser();
  const search = useMemo(() => ({ account_id: account?.id }), [account?.id]);

  const queryOptions = useRouteContextQuery((s) => s.listReceivedAccountInvitesQuery);
  const { data: invitations } = useQuery({
    ...queryOptions,
    refetchInterval: 30_000,
  });

  const invitationCount = invitations?.length ?? 0;

  const matchRoute = useMatchRoute();
  const isUserRouteMatch =
    !matchRoute({ from: '/accounts/$accountId', fuzzy: true }) && !matchRoute({ to: '/get-started' });
  const [open, setOpen] = useState(isUserRouteMatch);
  const toggleOpen = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (invitationCount > 0) {
      // oxlint-disable-next-line react/set-state-in-effect
      setOpen(true);
    }
  }, [invitationCount]);

  return (
    <Nav
      aria-label="User navigation"
      sx={{
        backgroundColor: open ? 'background.default' : 'background.paper',
        borderRadius: 1,
        gap: 0,
      }}
    >
      <ListItemButton
        onClick={toggleOpen}
        aria-expanded={open}
        dense={true}
        aria-controls="user-navigation"
        sx={(theme) => ({
          gap: 1,
          borderRadius: 1,
          bgcolor: isUserRouteMatch && !open ? theme.palette.primary._states.selected : 'inherit',
          ...(open
            ? {
                '&.MuiListItemButton-root': {
                  '&, &:hover, &:focus': {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                  },
                },
              }
            : {
                outline: '1px solid',
                outlineOffset: '-1px',
                outlineColor: 'action.selected',
              }),
        })}
      >
        <ListItemText
          data-sentry-mask={true}
          primary={userProfile.name ?? '-'}
          secondary={email}
          slotProps={{
            root: {
              sx: {
                width: 0,
              },
            },
            primary: {
              sx: sxTypography,
            },
            secondary: {
              sx: sxTypography,
            },
          }}
        />
        <NavItemToggleIcon open={open} />
      </ListItemButton>
      <Collapse in={open} timeout="auto" id="user-navigation" unmountOnExit={true} onEntered={onOpen}>
        <Stack
          sx={{
            px: 1,
            py: 1,
            borderTop: open ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <NavItem to="/preferences" icon={UserRoundCogIcon} search={search}>
            Preferences
          </NavItem>
          <NavItem to="/invitations" icon={SendHorizontalIcon} search={search} badgeContent={invitationCount}>
            Invitations
          </NavItem>
          <NavItem to="/security" icon={ShieldCheckIcon} search={search}>
            Security
          </NavItem>
          <NavItem to="/accounts" icon={UsersRoundIcon} search={search} activeLinkExact={true}>
            Accounts
          </NavItem>
          <Divider sx={{ mt: 2, mb: 1 }} />
          <NavItem to="/logout" icon={LogOutIcon}>
            Logout
          </NavItem>
        </Stack>
      </Collapse>
    </Nav>
  );
};
